import moment from "moment";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import { dateFormat } from "../../util/DateUtil";
import { currencyBRL } from "../../util/DoubleUtil";
import Loading from "../UI/Loading";

/** Parcela em aberto com vencimento anterior a hoje (datas vêm em UTC). */
export const isVencido = (lancamento) =>
  !lancamento.quitado &&
  Boolean(lancamento.dataVencimento) &&
  moment.utc(lancamento.dataVencimento).format("YYYY-MM-DD") < moment().format("YYYY-MM-DD");

const status = (lancamento) => {
  if (lancamento.quitado) return { label: "Quitado", color: "success" };
  if (isVencido(lancamento)) return { label: "Vencido", color: "error" };
  return { label: "A vencer", color: "default" };
};

const LancamentoTable = ({ lancamentos, isLoading, emptyText, onSelect }) => {
  if (isLoading) {
    return (
      <Box py={6}>
        <Loading description="Carregando lançamentos..." />
      </Box>
    );
  }

  if (lancamentos.length === 0) {
    return (
      <Box
        sx={{
          py: 6,
          textAlign: "center",
          color: "text.secondary",
          border: 1,
          borderStyle: "dashed",
          borderColor: "divider",
          borderRadius: 2,
        }}
      >
        <ReceiptLongOutlinedIcon fontSize="large" />
        <Typography variant="body2" mt={1}>
          {emptyText}
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer sx={{ border: 1, borderColor: "divider", borderRadius: 2 }}>
      <Table size="small" sx={{ minWidth: 800, whiteSpace: "nowrap" }} aria-label="Lançamentos do aluno">
        <TableHead>
          <TableRow sx={{ "& th": { fontWeight: 600, bgcolor: "action.hover" } }}>
            <TableCell>Vencimento</TableCell>
            <TableCell>Histórico</TableCell>
            <TableCell>Cód. Doc.</TableCell>
            <TableCell align="right">Valor</TableCell>
            <TableCell>Desconto</TableCell>
            <TableCell>Pagamento</TableCell>
            <TableCell>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {lancamentos.map((lancamento) => {
            const s = status(lancamento);
            return (
              <TableRow key={lancamento.codigo} hover onClick={() => onSelect(lancamento)} sx={{ cursor: "pointer" }}>
                <TableCell>
                  <Typography variant="body2">{dateFormat(lancamento.dataVencimento)}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Ano letivo {lancamento.anoLetivo}
                  </Typography>
                </TableCell>
                <TableCell>{lancamento.historicoLancamento?.descricao ?? "--"}</TableCell>
                <TableCell>{lancamento.codDoc ?? "--"}</TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight={600}>
                    {currencyBRL(lancamento.valor)}
                  </Typography>
                </TableCell>
                <TableCell>
                  {lancamento.valorDesconto ? (
                    <>
                      <Typography variant="body2">{currencyBRL(lancamento.valorDesconto)}</Typography>
                      {lancamento.dataDesconto && (
                        <Typography variant="caption" color="text.secondary">
                          até {dateFormat(lancamento.dataDesconto)}
                        </Typography>
                      )}
                    </>
                  ) : (
                    "--"
                  )}
                </TableCell>
                <TableCell>
                  {lancamento.dataPagamento || lancamento.valorPago ? (
                    <>
                      <Typography variant="body2">
                        {lancamento.valorPago ? currencyBRL(lancamento.valorPago) : "--"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {[lancamento.dataPagamento && dateFormat(lancamento.dataPagamento), lancamento.formaPago]
                          .filter(Boolean)
                          .join(" · ")}
                      </Typography>
                    </>
                  ) : (
                    "--"
                  )}
                </TableCell>
                <TableCell>
                  <Chip size="small" variant="outlined" label={s.label} color={s.color} />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default LancamentoTable;
